/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { usePagination } from 'src/core/hooks/usePagination';
import { QnaMakerQnaSwagger, QnaMakerSynonymSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import {
    qnaMakerQnaSwaggerToQnaStore,
    qnaMakerSynonymSwaggerToSynonymStore
} from 'src/qnaMaker/modules/handlers/qnas/qnaMakerAssetsMappers';

type AssetsType = 'qnas' | 'synonyms';

type QnaAssetsSwagger = QnaMakerQnaSwagger | QnaMakerSynonymSwagger;

type useQnaPaginatedAssetsParams = { assets: QnaAssetsSwagger[]; pageSize: number; assetsType?: AssetsType };

/**
 * A hook for managing the pagination in qna pairs.
 *
 * @param assets The total items to be managed.
 * @param pageSize The default page size.
 * @returns The required information to managing the pagination.
 */
export const useQnaPaginatedAssets = (params: useQnaPaginatedAssetsParams) => {
    const { assets, pageSize, assetsType = 'qnas' } = params;
    const { currentPage, setCurrentPage, pageItems, pageCount } = usePagination(assets, pageSize);

    const items =
        assetsType === 'qnas'
            ? React.useMemo(() => pageItems.map(qnaMakerQnaSwaggerToQnaStore), [assets, pageItems])
            : React.useMemo(() => pageItems.map(qnaMakerSynonymSwaggerToSynonymStore), [assets, pageItems]);

    return {
        items,
        pageCount,
        currentPage,
        onPageChange: setCurrentPage
    };
};
